import './user-applications.scss';
import {MutableRefObject, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {AffiliationInfo} from '../../backend/interfaces/affiliation-info';
import {CaregiverMode} from '../../enums/caregiver-mode';
import {translations} from '../../i18n/translations';
import {UsersRepository} from '../../repositories/users';
import {LoadingContent} from '../loading-content/loading-content';
import {Alert, Box, Stack} from '@mui/material';
import GenericHeader from '../generic-header/generic-header';
import SearchInput from '../search-input/search-input';
import {UserApplicationsTable} from './user-applications-table';
import {ApplicationsRepository} from '../../repositories/applications';
import {CategorizedApplication} from '../../backend/responses/categorized-application';
import {PaginatedTableData} from '../../interfaces/paginated-table-data';
import {usePaginatedTable} from '../../hooks';
// Hold variables
const BATCH_SIZE: number = 10;

/**
 * Listing of all the applications associated with a user.
 * On this screen, it's possible to enable/disable an app.
 *
 * System admins have an affiliation filter to get the applications.
 *
 * Permissions are given by props.
 */
type UserApplicationsProps = {
  caregiverMode: CaregiverMode.Professional | CaregiverMode.SystemAdministrator;
  canWrite: boolean;
  userId: string;
  userName: string;
  applicationRowBaseLinkUrl: string;
  entityId?: string;
  headerTitleCrumbs?: {label: string; link: string}[];
};

// Columns IDs
export enum ColumnIdentifier {
  Name = 'name',
  Category = 'category',
  State = 'state',
}

export const UserApplications = ({
  caregiverMode,
  canWrite,
  userId,
  userName,
  applicationRowBaseLinkUrl,
  entityId,
  headerTitleCrumbs,
}: UserApplicationsProps) => {
  // Hold state
  const [userAffiliations, setUserAffiliations] = useState<
    | {
        affiliations: Array<AffiliationInfo>;
        totalAffiliations: number;
        selectedAffiliation?: AffiliationInfo;
      }
    | undefined
  >();
  const [entity, setEntity] = useState<{entityId: string; entityName?: string} | undefined>(
    caregiverMode === CaregiverMode.SystemAdministrator
      ? userAffiliations?.selectedAffiliation?.entity.id
        ? {
            entityId: userAffiliations.selectedAffiliation.entity.id,
            entityName: userAffiliations.selectedAffiliation.entity.name,
          }
        : undefined
      : entityId
        ? {entityId: entityId}
        : undefined
  );
  const {t} = useTranslation();
  const entityRef: MutableRefObject<
    | {
        entityId: string;
        entityName?: string;
      }
    | undefined
  > = useRef(entity);

  const handleGetApplications = async (limit: number, skip: number, search?: string) => {
    // Check entity id
    if (!entityRef.current?.entityId) {
      // Error
      throw new Error('Empty entity id.');
    }

    // Load applications
    const result: {totalCount: number; data: Array<CategorizedApplication>} =
      await ApplicationsRepository.instance.getForUserInEntity(
        userId,
        entityRef.current?.entityId,
        skip,
        limit,
        search
      );

    // Process data
    const processedData: PaginatedTableData<CategorizedApplication> = result.data.map((obj) => {
      // Return row values
      return {
        id: obj.application.id || '',
        item: obj,
        avatarUrl: obj.application.picture,
        enabled: obj.application.isActive,
        toLink:
          applicationRowBaseLinkUrl.charAt(applicationRowBaseLinkUrl.length - 1) === '/'
            ? `${applicationRowBaseLinkUrl}${obj.application.id}`
            : `${applicationRowBaseLinkUrl}/${obj.application.id}`,
        columns: [
          {
            columnId: ColumnIdentifier.Name,
            // The name is localized
            value: obj.application.name || '',
          },
          {
            columnId: ColumnIdentifier.Category,
            // The name is localized
            value: obj.category.name || '',
          },
          {
            columnId: ColumnIdentifier.State,
            value: obj.application.isActive
              ? t(translations.user_applications.state_value_true)
              : t(translations.user_applications.state_value_false),
          },
        ],
      };
    });

    // Return result
    return {
      data: processedData,
      totalCount: result.totalCount,
    };
  };

  const {currentSearch, handleUpdateCurrentSearch, handleApplyFilters} =
    usePaginatedTable<CategorizedApplication>({
      fetchData: handleGetApplications,
    });

  useEffect(() => {
    if (entity?.entityId !== entityRef.current?.entityId) {
      // Reset
      entityRef.current = entity;

      // Fetch data and reset page count
      handleApplyFilters(1, currentSearch);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity]);

  // Get the first batch of user affiliations
  useEffect(() => {
    const getUserAffiliations = async () => {
      try {
        // Get first batch of affiliations
        const result = await UsersRepository.instance.getUserAffiliations(userId, 0, BATCH_SIZE);

        // Set affiliations
        if (result) {
          setUserAffiliations({
            affiliations: result.data,
            selectedAffiliation: result.data[0],
            totalAffiliations: result.totalCount,
          });
          setEntity({
            entityId: result.data[0].entity.id,
            entityName: result.data[0].entity.name,
          });
          entityRef.current = entity;
        } else {
          // Error
          throw new Error('Empty user affiliations.');
        }
      } catch (error) {
        // Log
        console.debug('🔴 Could not get the user affiliations.', error);

        // Set user affiliations
        setUserAffiliations({
          affiliations: [],
          totalAffiliations: 0,
        });
      }
    };

    if (caregiverMode === CaregiverMode.SystemAdministrator) {
      getUserAffiliations();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caregiverMode, userId]);

  return (
    <Box display="flex" flexDirection="column" flexGrow={1}>
      {caregiverMode === CaregiverMode.Professional && entityId && headerTitleCrumbs && (
        <GenericHeader
          titleCrumbs={headerTitleCrumbs}
          isLoading={false}
          leftColumn={
            <Stack
              gap={2}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                width: '100%',
                paddingTop: '10px',
              }}
            >
              <SearchInput
                placeholder={t(translations.user_applications.search_input_placeholder)}
                value={currentSearch}
                onChange={(event) => handleUpdateCurrentSearch(event.target.value)}
                onSearchAction={(value: string) => handleApplyFilters(1, value)}
              />
            </Stack>
          }
          rightColumn={null}
        />
      )}
      <Box
        className={`listing-container${caregiverMode === CaregiverMode.SystemAdministrator ? '-sys' : ''}`}
      >
        <Box className="listing-container-centered">
          {caregiverMode === CaregiverMode.SystemAdministrator &&
            (!userAffiliations || !entityRef.current) && <LoadingContent marginTop="32px" />}
          {((caregiverMode === CaregiverMode.Professional && !entityId) ||
            (caregiverMode === CaregiverMode.SystemAdministrator &&
              userAffiliations &&
              userAffiliations.affiliations.length === 0)) && (
            <Alert severity="info" className="user-applications-info-message">
              {t(translations.user_applications.no_affiliations_info_message)}
            </Alert>
          )}
          {((caregiverMode === CaregiverMode.Professional && entityId) ||
            (caregiverMode === CaregiverMode.SystemAdministrator &&
              userAffiliations &&
              userAffiliations.affiliations.length > 0 &&
              userAffiliations.selectedAffiliation &&
              entityRef.current)) && (
            <UserApplicationsTable
              caregiverMode={caregiverMode}
              canWrite={canWrite}
              userId={userId}
              userName={userName}
              entity={entity}
              getApplications={handleGetApplications}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};
